import { useRef, useState } from 'react';
import gsap from 'gsap';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';
import Heading from 'components/atoms/heading/Heading';
import Text from 'components/atoms/text/Text';
import PercentageGauge, { normalizePercentage } from './PercentageGauge';
import type { CampaignStatistic as Statistic } from './Campaign.data';
import styles from './Campaign.module.scss';

export default function CampaignStatistic({
  item,
  animate,
}: {
  item: Statistic;
  animate: boolean;
}) {
  const target = normalizePercentage(item.percentage);
  const [value, setValue] = useState(target);
  const ref = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element || !animate || !('IntersectionObserver' in window)) {
      setValue(target);
      return;
    }
    const media = gsap.matchMedia();
    media.add(
      {
        motion: '(prefers-reduced-motion: no-preference)',
        reduced: '(prefers-reduced-motion: reduce)',
      },
      (context) => {
        if (context.conditions?.reduced) {
          setValue(target);
          return;
        }
        setValue(0);
        const counter = { value: 0 };
        let tween: gsap.core.Tween | undefined;
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            tween = gsap.to(counter, {
              value: target,
              duration: 1.4,
              ease: 'power2.out',
              onUpdate: () => setValue(counter.value),
              onComplete: () => setValue(target),
            });
          },
          { threshold: 0.35 },
        );
        observer.observe(element);
        return () => {
          observer.disconnect();
          tween?.kill();
        };
      },
    );
    return () => media.revert();
  }, [target, animate]);

  return (
    <div ref={ref} className={styles.statisticContent}>
      <PercentageGauge percentage={value} />
      <Heading
        as="p"
        size="heading-04"
        className={styles.statisticValue}
        transitionInType="none"
        disableTriggerInOnScroll
      >
        <span aria-label={`${target}%`}>
          <span aria-hidden="true">{value === target ? target : Math.round(value)}%</span>
        </span>
      </Heading>
      <Text
        as="p"
        size="body-standard"
        className={styles.statisticDescription}
        transitionInType="none"
        disableTriggerInOnScroll
      >
        {item.description}
      </Text>
    </div>
  );
}
